import { useCallback, useEffect, useRef, useState } from "react";
import { FiCamera, FiTrash2, FiUpload } from "react-icons/fi";

import CameraCaptureModal from "@/components/camera-capture-modal";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown";

import { formatName } from "@/lib/format-data";
import { cx } from "@/lib/tailwind-utils";
import { useToast } from "@/lib/use-toast";

import { getSignedUrl } from "@/api/auth/gcs";
import { useUpdatePatientProfilePictureMutation } from "@/api/provider/user/patient/patient-mutation";

interface ProfilePicturePatient {
  profilePictureUrl?: string;
  firstName?: string;
  lastName?: string;
  id: string;
}

interface ProfilePictureUploadProps {
  patient: ProfilePicturePatient;
  onUploadSuccess?: (profilePictureUrl: string) => void;
  className?: string;
  // Network-layer overrides. Sessions without provider credentials (the patient
  // portal) supply their own transport; staff surfaces get the defaults.
  uploadFile?: (file: File | null) => Promise<string | null>;
  resolveDisplayUrl?: (storedPath: string) => Promise<string | null>;
  // Camera capture pulls the provider-scoped gallery for ghost references, so
  // it is opt-out for patient-facing surfaces.
  allowCameraCapture?: boolean;
}

export default function ProfilePictureUpload({
  patient,
  className,
  onUploadSuccess,
  uploadFile,
  resolveDisplayUrl,
  allowCameraCapture = true,
}: ProfilePictureUploadProps) {
  const { toast } = useToast();
  const updateProfilePictureMutation = useUpdatePatientProfilePictureMutation(
    patient.id,
  );
  const submitFile = useCallback(
    (file: File | null) =>
      uploadFile
        ? uploadFile(file)
        : updateProfilePictureMutation.mutateAsync({ file }),
    [uploadFile],
  );
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const [showCameraModal, setShowCameraModal] = useState(false);
  const [isImageLoading, setIsImageLoading] = useState(false);
  const [isImageReady, setIsImageReady] = useState(false);

  // Preload image and get signed URL
  useEffect(() => {
    const loadImage = async () => {
      if (patient.profilePictureUrl) {
        try {
          setIsImageLoading(true);
          setIsImageReady(false);

          const displayUrl = resolveDisplayUrl
            ? await resolveDisplayUrl(patient.profilePictureUrl)
            : (await getSignedUrl(patient.profilePictureUrl))?.signedUrl;
          if (!displayUrl) {
            setIsImageLoading(false);
            return;
          }

          // Preload the image
          const img = new Image();
          img.onload = () => {
            setSignedUrl(displayUrl);
            setIsImageReady(true);
            setIsImageLoading(false);
          };
          img.onerror = () => {
            console.error("Failed to load profile picture");
            setIsImageLoading(false);
            setIsImageReady(false);
          };
          img.src = displayUrl;
        } catch (error) {
          console.error("Failed to load profile picture:", error);
          setIsImageLoading(false);
          setIsImageReady(false);
        }
      } else {
        setSignedUrl(null);
        setIsImageReady(false);
        setIsImageLoading(false);
      }
    };

    loadImage();
  }, [patient.profilePictureUrl, resolveDisplayUrl]);

  const handleFileSelect = useCallback(
    async (file: File) => {
      // Validate file type
      const allowedTypes = [
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/webp",
      ];
      if (!allowedTypes.includes(file.type)) {
        toast({
          title: "Invalid file type",
          description: "Please upload a JPEG, PNG, or WebP image.",
          variant: "error",
        });
        return;
      }

      // Validate file size (5MB limit)
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        toast({
          title: "File too large",
          description: "Please upload an image smaller than 5MB.",
          variant: "error",
        });
        return;
      }

      setIsUploading(true);

      const profilePictureUrl = await submitFile(file);

      if (profilePictureUrl) {
        onUploadSuccess?.(profilePictureUrl);
        toast({
          title: "Success",
          description: "Profile picture updated successfully.",
          variant: "success",
        });
      } else {
        toast({
          title: "Upload failed",
          description: "Failed to upload profile picture. Please try again.",
          variant: "error",
        });
      }

      setIsUploading(false);
    },
    [patient.id, toast, submitFile],
  );

  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) {
        handleFileSelect(file);
      }
      // Reset the input value so the same file can be selected again
      event.target.value = "";
    },
    [handleFileSelect],
  );

  const handleFileUpload = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleTakePhoto = useCallback(() => {
    setShowCameraModal(true);
  }, []);

  const handleCameraCapture = useCallback(
    (file: File | Blob) => {
      if (file instanceof File) {
        handleFileSelect(file);
      } else {
        const capturedFile = new File([file], `photo-${Date.now()}.jpg`, {
          type: "image/jpeg",
        });
        handleFileSelect(capturedFile);
      }
    },
    [handleFileSelect],
  );

  const handleRemovePhoto = useCallback(async () => {
    setIsUploading(true);
    setSignedUrl(null);
    setIsImageReady(false);

    const result = await submitFile(null);

    if (result === "") {
      onUploadSuccess?.("");
      toast({
        title: "Success",
        description: "Profile picture removed successfully.",
        variant: "success",
      });
    } else {
      toast({
        title: "Remove failed",
        description: "Failed to remove profile picture. Please try again.",
        variant: "error",
      });
    }

    setIsUploading(false);
  }, [patient.id, toast, onUploadSuccess, submitFile]);

  return (
    <div className={cx("relative", className)}>
      <div className="group relative">
        {isUploading || isImageLoading ? (
          <div className="h-12 w-12 animate-pulse rounded-full bg-muted" />
        ) : isImageReady && signedUrl ? (
          <img
            src={signedUrl}
            alt={`${formatName(patient.firstName, patient.lastName)}`}
            className="h-12 w-12 rounded-full object-cover"
          />
        ) : patient.profilePictureUrl ? (
          <div className="h-12 w-12 animate-pulse rounded-full bg-muted" />
        ) : (
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary text-body-lg font-normal text-white">
            {patient.firstName?.charAt(0) || <span>{""}</span>}
            {patient.lastName?.charAt(0) || <span>{""}</span>}
          </div>
        )}

        {!isUploading && !isImageLoading && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                disabled={isUploading || isImageLoading}
                className="absolute inset-0 flex h-12 w-12 items-center justify-center rounded-full bg-black/50 opacity-0 transition-opacity group-hover:opacity-100 hover:bg-black/60"
                aria-label="Profile picture options"
              >
                <FiCamera className="h-4 w-4 text-white" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" side="bottom">
              {allowCameraCapture && (
                <DropdownMenuItem onSelect={handleTakePhoto}>
                  <FiCamera className="mr-2 h-4 w-4" />
                  Take Photo
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onSelect={handleFileUpload}>
                <FiUpload className="mr-2 h-4 w-4" />
                Upload Photo
              </DropdownMenuItem>
              {(signedUrl || patient.profilePictureUrl) && (
                <DropdownMenuItem onSelect={handleRemovePhoto}>
                  <FiTrash2 className="mr-2 h-4 w-4" />
                  Remove Photo
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/jpg,image/png,image/webp"
        onChange={handleFileChange}
        className="hidden"
      />

      {allowCameraCapture && (
        <CameraCaptureModal
          open={showCameraModal}
          onOpenChange={setShowCameraModal}
          onCapture={handleCameraCapture}
          patientId={patient.id}
        />
      )}
    </div>
  );
}
